/**
 * Changement de type de compte depuis « Mon profil » — logique PURE testée
 * + un hook de lecture.
 *
 * Tout utilisateur (élève, étudiant, parent, professeur, curieux) peut changer
 * de type de compte, SAUF un élève de moins de 15 ans ou compte enfant
 * géré par un parent. Il repasse par l'onboarding (`/onboarding?changer=1`)
 * sur l'étape « rôle », puis par l'étape propre au nouveau type (niveau,
 * matières, consentement parental…).
 *
 * La règle est APPLIQUÉE en base (trigger `guard_minor_role_change`, migrations
 * 0313-0314) : ce module n'en est que le miroir d'affichage.
 */

import { useQuery } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { PARENTAL_CONSENT_AGE_THRESHOLD } from '@jeraisonne/db';
import { computeAgeYears } from '@/lib/age';

export const ACCOUNT_TYPE_CHANGE_PARAM = 'changer';
export const ACCOUNT_TYPE_CHANGE_HREF = `/onboarding?${ACCOUNT_TYPE_CHANGE_PARAM}=1`;
/** En dessous de cet âge, pas de changement de type (= seuil du consentement parental, miroir de 0314). */
export const ACCOUNT_TYPE_CHANGE_MIN_AGE = PARENTAL_CONSENT_AGE_THRESHOLD;

const CHANGEABLE_ROLES = ['student', 'parent', 'tutor', 'player'] as const;

/** Libellé + emoji du type de compte actuel (mêmes que les cartes de l'onboarding). */
export const ACCOUNT_TYPE_LABELS: Record<string, string> = {
  student: '🎒 Élève',
  parent: '👨‍👩‍👧 Parent',
  tutor: '🧑‍🏫 Professeur',
  player: '🎮 Curieux',
};

export interface MinorInfo {
  birthdate: string | null;
  managedByParent: boolean;
}

/** Bloqué = compte enfant géré par un parent, ou date de naissance < 15 ans. */
export function isMinorAccount(
  info: MinorInfo | null | undefined,
  now: Date = new Date(),
): boolean {
  if (!info) return false;
  if (info.managedByParent) return true;
  if (!info.birthdate) return false;
  const age = computeAgeYears(info.birthdate, now);
  return age !== null && age < ACCOUNT_TYPE_CHANGE_MIN_AGE;
}

/** Le rôle peut-il changer de type (hors admin) ? `minor` : infos d'âge connues. */
export function canChangeAccountType(
  role: string | null | undefined,
  minor?: MinorInfo | null,
  now: Date = new Date(),
): boolean {
  if (!role || !(CHANGEABLE_ROLES as readonly string[]).includes(role)) return false;
  return !isMinorAccount(minor, now);
}

/**
 * Vrai si l'onboarding est ouvert pour changer de type : paramètre présent
 * (chaîne de requête ou objet de recherche du routeur) ET rôle éligible.
 * L'âge est vérifié à part (lecture asynchrone, cf. useAccountTypeChangeAllowed).
 */
export function isAccountTypeChange(
  search: string | Record<string, unknown> | null | undefined,
  role: string | null | undefined,
): boolean {
  if (!canChangeAccountType(role) || search == null) return false;
  const value =
    typeof search === 'string'
      ? new URLSearchParams(search).get(ACCOUNT_TYPE_CHANGE_PARAM)
      : search[ACCOUNT_TYPE_CHANGE_PARAM];
  return value != null && String(value) === '1';
}

/** Message lisible d'une erreur de changement de type (trigger 0313). */
export function translateAccountTypeChangeError(message: string): string {
  if (message.includes('minor_role_change_forbidden')) {
    return 'Avant 15 ans, on ne peut pas changer de type de compte.';
  }
  return message || 'Erreur inattendue.';
}

/**
 * Le compte connecté peut-il changer de type ? Lit sa date de naissance et le
 * rattachement « compte enfant » (student_profiles, ligne absente = adulte
 * inconnu → autorisé, comme en base). `allowed` vaut null tant que ça charge.
 */
export function useAccountTypeChangeAllowed(
  userId: string | null | undefined,
  role: string | null | undefined,
): { allowed: boolean | null } {
  const eligibleRole = canChangeAccountType(role);
  const query = useQuery({
    queryKey: ['account-type-change', userId],
    enabled: !!userId && eligibleRole,
    staleTime: 60_000,
    queryFn: async (): Promise<MinorInfo | null> => {
      // `managed_by_parent` (0301) est postérieur aux types générés.
      const { data, error } = await (supabase as unknown as SupabaseClient)
        .from('student_profiles')
        .select('birthdate, managed_by_parent')
        .eq('id', userId!)
        .maybeSingle();
      if (error) throw error;
      if (!data) return null;
      const row = data as { birthdate: string | null; managed_by_parent: string | null };
      return { birthdate: row.birthdate, managedByParent: row.managed_by_parent != null };
    },
  });
  if (!eligibleRole) return { allowed: false };
  if (query.isPending) return { allowed: null };
  // Lecture en échec : on n'affiche pas l'option (la base garde de toute façon).
  if (query.isError) return { allowed: false };
  return { allowed: canChangeAccountType(role, query.data) };
}
